import { useEffect, useRef, useState } from "react";
import { Arrow } from "../svgs/Svgs";
import './InputDropdown.scss';
import { showToastMessage } from "../../../utils/commonFunctions";

interface Option {
    label: string;
    value: string | number;
}

type InputDropdownProps = {
    allOptions: any[];
    placeholder?: string;
    setFinalValue: (val: any) => void;
    defaultValue: any;
    resetKey?: boolean | string; // optional
};

const InputDropdown = ({allOptions, placeholder, setFinalValue, defaultValue, resetKey}: InputDropdownProps) => {
    const [openDropDown, setOpenDropDown] = useState(false)
    const [inputValue, setInputValue] = useState('')
    const [selectedOption, setSelectedOption] = useState<null | Option>(null)
    const controlRef = useRef<HTMLDivElement>(null)

    //FOR RESETTING THE DEFAULT VALUE
    useEffect(() => {
        if (defaultValue){
            setSelectedOption(defaultValue)
            setInputValue('')
        }
    }, [resetKey])

    //HANDLE SELECT FILE DROPDOWN
    useEffect(() => {
    const clickOutside = (e) => {
        
    const options = controlRef.current
        if (options && options.contains(e.target)) return
        if (openDropDown) {
            setOpenDropDown(false)
        }
    }
    document.addEventListener('mousedown', clickOutside)
    return () => {
        document.removeEventListener('mousedown', clickOutside)
    }
    }, [openDropDown])

    //TO SET THE FINAL VALUE OF DROPDOWN
    useEffect(() => {
        if (selectedOption) {
            setFinalValue(selectedOption)
        } else if (inputValue !== '') {
            setFinalValue({ label: inputValue, value: inputValue })
        } else {
            setFinalValue(null)
        }

    }, [selectedOption, inputValue])

    //ON CLICK OF CONTROL
    const handleControl = () => {
        setOpenDropDown((prev) => !prev);
    }

    //ON CHANGING INPUT
    const onInputChange = (value) => {
        setInputValue(value)
        filteredOptions(value)
        setSelectedOption(null)
    }

    //FILTER OPTIONS WHILE SEARCHING
    const filteredOptions = (name: string) => {
        const search = name?.toLowerCase();
        return Array.from((allOptions || []) as Option[]).filter(option => {
            const nameMatch = option.label?.toLowerCase().includes(search);
            return nameMatch;
        });
    };

    //HANDLE OPTION CLICK
    const handleOptionClick = (option) => {
        setSelectedOption(option)
        setOpenDropDown(false);
        setInputValue('')
    }

    // HANDLE INPUT CHANGE
    const handleInputChange = (value: string) => {
        const rawValue = value;

        // Check if the input has any invalid characters (not a number)
        if (/[^0-9]/.test(rawValue)) {
            showToastMessage('error', 'Only numbers are allowed');
        }

        // Remove invalid characters
        const filteredValue = rawValue.replace(/[^0-9]/g, '');

        // Call the parent callback with filtered value
        onInputChange?.(filteredValue);
    };

    //DISPLAY SELECTED OPTION TEXT
    const displayText = selectedOption
        ? selectedOption?.label
        : inputValue !== ''
            ? inputValue
            : placeholder;

    return(
        <div className='input__dropdown__wrapper' ref={controlRef}>
            <div
                className={`input__dropdown__control ${(selectedOption || inputValue !== '') ? 'haveValue' : ''}`}
                onClick={handleControl}
            >
                {/* Dropdown title */}
                <div
                    className={`input__dropdown__control__title text-truncate ${(selectedOption || inputValue !== '') ? 'haveValue' : ''}`}
                    title={displayText}
                >
                    {displayText}
                </div>

                {/* Dropdown arrow - direction depends on open state */}
                <Arrow
                    direction={openDropDown ? 'left' : 'down'}
                    size={13}
                />
            </div>

            {/* DROPDOWN */}
            <div className={`input__dropdown__body ${openDropDown ? '' : 'd-none'}`}>

                {/* SEARCH / INPUT  */}
                <input
                    type="text"
                    className="input__dropdown__search"
                    value={inputValue}
                    placeholder="Select/Enter"
                    onChange={(e) => handleInputChange(e.target.value)}
                />
           
                {/* OPTIONS */}
                <div className="input__dropdown__options__body">
                    {/* Map through filtered options */}
                    {filteredOptions(inputValue)?.map((option) => (
                        <div
                            key={option?.label}
                            className={`input__dropdown__option__row ${selectedOption?.label === option?.label ? 'active' : ''}`}
                            onClick={() => handleOptionClick(option)}
                        >
                            <span 
                                className="text-truncate" 
                                title={option?.label}
                            >
                                {option?.label}
                            </span>
                        </div>
                    ))}

                    {/* Show message if no options are available */}
                    {filteredOptions(inputValue)?.length === 0 && (
                        <div className="input_no_options_container">
                            No options available
                        </div>
                    )}
                </div>
            </div>
        </div>
    )
}

export default InputDropdown;